import { NocApiClient } from '@nocowanie/noc-api';

import { dateHelpers } from '@nocowanie/core/helpers';

import { SORT_VALUES } from '@app/consts/search-filters.consts';
import { AmenityApiMapping, PropertyTypeApiMapping } from '@app/enums';
import { environment } from '@app/environments/environment';
import { AccommodationTilesResponse } from '@app/helpers/accommodation-tiles.helpers';
import { convertFiltersToApiParams } from '@app/helpers/filter-mapping.helpers';
import {
    isIgnoredPropertyTypeCheck,
    isSupportedAmenityCheck,
} from '@app/helpers/listing-mapping.helpers';
import {
    convertReservationFeaturesToBookingOptions,
    mapRatingToApi,
} from '@app/helpers/map-api-params.helpers';
import { SearchCriteria, SearchFilters } from '@app/models/search.model';

import type { TFunction } from 'next-i18next';

export const filterUnsupportedFilters = (filters: SearchFilters) => {
    return {
        ...filters,
        amenities: filters.amenities?.filter(amenity => isSupportedAmenityCheck(parseInt(amenity))),
    };
};

export const getListingTiles = async ({
    searchCriteria,
    searchFilters,
    campaignListingId,
    noPrice = false,
    page = 1,
}: {
    searchCriteria: Partial<SearchCriteria>;
    searchFilters: SearchFilters;
    noPrice?: boolean;
    page?: number;
    campaignListingId?: string;
}): Promise<AccommodationTilesResponse | null> => {
    try {
        const {
            propertyType,
            amenities,
            rating,
            provision,
            reservationFeatures,
            sort,
            priceFrom,
            priceTo,
            onlineBooking,
        } = searchFilters;

        const {
            checkInDate,
            checkOutDate,
            flexDates,
            poiId,
            geoId,
            locationType,
            guests: { adultsCount, childrenAges, childrenCount } = {},
        } = searchCriteria;

        const parsedOnlineBooking =
            typeof onlineBooking === 'string' ? parseInt(onlineBooking) : undefined;
        const isCampaignListing = !!campaignListingId;
        const sorting = sort ? SORT_VALUES[sort as keyof typeof SORT_VALUES] : undefined;

        const {
            propertyType: parsedPropertyType,
            amenities: parsedAmenities,
            provision: parsedProvision,
        } = convertFiltersToApiParams({
            propertyTypeIds: propertyType
                ?.map(id => parseInt(id))
                .filter(propertyType => !isIgnoredPropertyTypeCheck(propertyType)),
            amenityIds: amenities
                ?.map(id => parseInt(id))
                .filter(amenity => isSupportedAmenityCheck(amenity)),
            provisionIds: provision?.map(id => parseInt(id)),
        });

        const parsedReservationFeatures =
            convertReservationFeaturesToBookingOptions(reservationFeatures);

        const apiClient = new NocApiClient({ BASE: environment.NOC_API_END_POINT });
        const response = await apiClient.listingTile.accommodationListingTiles(
            page,
            adultsCount,
            undefined,
            Object.assign({}, parsedAmenities) as any,
            parsedOnlineBooking === 1 || parsedOnlineBooking === 0
                ? parsedOnlineBooking
                : undefined,
            Object.assign({}, parsedReservationFeatures) as any,
            Object.assign({}, parsedPropertyType) as any,
            childrenCount,
            Object.assign({}, childrenAges),
            checkInDate ? dateHelpers.format(new Date(checkInDate), 'yyyy-MM-dd') : undefined,
            checkOutDate ? dateHelpers.format(new Date(checkOutDate), 'yyyy-MM-dd') : undefined,
            undefined,
            Object.assign({}, parsedProvision) as any,
            rating ? mapRatingToApi(rating) : undefined,
            geoId && locationType === 'region' ? parseInt(geoId) : undefined,
            geoId && locationType === 'voivodeship' ? parseInt(geoId) : undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            sorting
                ? ({
                      [sorting.type]: sorting?.order,
                  } as unknown as string[])
                : undefined,
            geoId && locationType === 'city' ? parseInt(geoId) : undefined,
            geoId && locationType === 'country' ? parseInt(geoId) : undefined,
            flexDates,
            noPrice ? 1 : undefined,
            isCampaignListing ? 'campaign-listings' : undefined,
            isCampaignListing ? 'accommodationsIDs' : undefined,
            isCampaignListing ? campaignListingId : undefined,
            poiId ? `${poiId}` : undefined,
            priceFrom ?? undefined,
            priceTo ?? undefined,
        );

        return {
            tiles: response['hydra:member'] || [],
            totalItems: response['hydra:totalItems'],
            nopTotalItems: response['hydra:nopTotalItems'],
            realTimeItems: response['hydra:realTimeItems'],
            partial: response['hydra:partial'],
        };
    } catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Failed to fetch listing data');
    }
};

export const resolveListingHeader = (
    t: TFunction,
    params: {
        locationName: string;
        propertyTypeId?: PropertyTypeApiMapping;
        amenityId?: AmenityApiMapping;
    },
) => {
    const { locationName, propertyTypeId, amenityId } = params;

    if (propertyTypeId && amenityId) {
        return `${locationName} ${t(`propertyTypes.${propertyTypeId}.header`)} ${t(`amenities.${amenityId}.header`)}`;
    }

    if (propertyTypeId) {
        return `${locationName} ${t(`propertyTypes.${propertyTypeId}.header`)}`;
    }

    if (amenityId) {
        return `${locationName} ${t('pageTitleSuffix')} ${t(`amenities.${amenityId}.header`)}`;
    }

    return `${locationName} ${t('pageTitleSuffix')}`;
};
